import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AuthenticationService } from '../../authentication/authentication.service';

export interface OpenAIModel {
    id: string;
    priceTier: string;
    costOrder: number;
    description: string;
    recommended: boolean;
}

@Component({
    selector: 'app-openai-settings',
    templateUrl: './openai-settings.component.html',
    styleUrls: ['./openai-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class OpenAISettingsComponent implements OnInit, OnDestroy {
    configured = false;
    loading = false;
    apiKeyInput = '';
    message = '';

    // Model selector
    selectedModel = 'gpt-4o-mini';
    availableModels: OpenAIModel[] = [];
    loadingModels = false;

    private subscriptions = new Subscription();
    httpOptions: any = {};

    constructor(private http: HttpClient, private auth: AuthenticationService, private toastr: ToastrService) {
        this.httpOptions = this.auth.getHeaders();
    }

    ngOnInit(): void {
        this.refreshStatus();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    refreshStatus(): void {
        this.loading = true;
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        const sub = this.http.get<{ configured: boolean }>(`${environment.baseUrl}/openai/key/status`, reqOptions).subscribe({
            next: (res) => {
                this.configured = !!res?.configured;
                this.refreshCurrentKey();
                if (this.configured) {
                    this.fetchCurrentModel();
                    this.fetchModels();
                }
            },
            error: () => {
                this.message = 'Kon status niet ophalen.';
                this.loading = false;
            },
        });
        this.subscriptions.add(sub);
    }

    private refreshCurrentKey(): void {
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        const sub = this.http.get<{ apiKey: string | null }>(`${environment.baseUrl}/openai/key`, reqOptions).subscribe({
            next: (res) => {
                this.apiKeyInput = (res?.apiKey || '').trim();
                this.loading = false;
            },
            error: () => {
                this.apiKeyInput = '';
                this.loading = false;
            },
        });
        this.subscriptions.add(sub);
    }

    saveKey(): void {
        if (!this.apiKeyInput || !this.apiKeyInput.trim()) {
            this.message = 'Voer een geldige API-sleutel in.';
            return;
        }
        this.loading = true;
        const body = { apiKey: this.apiKeyInput.trim() };
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        const sub = this.http.post(`${environment.baseUrl}/openai/key`, body, reqOptions).subscribe({
            next: () => {
                this.message = 'OpenAI-sleutel opgeslagen.';
                this.refreshStatus();
            },
            error: () => {
                this.message = 'Opslaan mislukt. Controleer de sleutel en rechten.';
                this.loading = false;
            },
        });
        this.subscriptions.add(sub);
    }

    clearKey(): void {
        this.loading = true;
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        const sub = this.http.delete(`${environment.baseUrl}/openai/key`, reqOptions).subscribe({
            next: () => {
                this.message = 'OpenAI-sleutel verwijderd.';
                this.apiKeyInput = '';
                this.availableModels = [];
                this.selectedModel = 'gpt-4o-mini';
                this.refreshStatus();
            },
            error: () => {
                this.message = 'Verwijderen mislukt.';
                this.loading = false;
            },
        });
        this.subscriptions.add(sub);
    }

    // ---- Model selector ----

    private fetchCurrentModel(): void {
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        this.subscriptions.add(
            this.http.get<{ model: string }>(`${environment.baseUrl}/openai/model`, reqOptions).subscribe({
                next: (res) => {
                    this.selectedModel = res?.model || 'gpt-4o-mini';
                },
            }),
        );
    }

    fetchModels(): void {
        this.loadingModels = true;
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        this.subscriptions.add(
            this.http.get<OpenAIModel[]>(`${environment.baseUrl}/openai/models`, reqOptions).subscribe({
                next: (models) => {
                    this.availableModels = models;
                    this.loadingModels = false;
                    if (this.selectedModel && !models.find((m) => m.id === this.selectedModel)) {
                        this.availableModels.unshift({
                            id: this.selectedModel,
                            priceTier: '?',
                            costOrder: 0,
                            description: 'Huidig geselecteerd model (niet meer beschikbaar)',
                            recommended: false,
                        });
                    }
                },
                error: () => {
                    this.loadingModels = false;
                },
            }),
        );
    }

    updateModel(modelId: string): void {
        const previous = this.selectedModel;
        this.selectedModel = modelId;
        const body = { model: modelId };
        const reqOptions = { headers: this.httpOptions.headers, observe: 'body' as const };
        this.subscriptions.add(
            this.http.put(`${environment.baseUrl}/openai/model`, body, reqOptions).subscribe({
                next: () => {
                    this.toastr.success(`Model gewijzigd naar ${modelId}`);
                },
                error: () => {
                    this.selectedModel = previous;
                    this.toastr.error('Kon model niet wijzigen.');
                },
            }),
        );
    }
}
